import type { Router } from 'vue-router';

import { Capacitor } from '@capacitor/core';

import { useIdleLock } from '@/composables/domains/mobile/useIdleLock';
import { exitApp, addBackButtonListener, removeBackButtonListener } from '@/utils/native/nativeBridge';

type Interceptor = () => boolean; // true 반환 시 뒤로 가기 소비

export function useButtonBackAndroid(
  router: Router,
  opts?: {
    topLevelRoutes?: string[]; // 예: ['mobile-dashboard']
    interceptors?: Interceptor[]; // 예: 모달 닫기
    onTopLevelBackHint?: () => void; // 예: '한 번 더 누르면 종료'
    topLevelExitDelayMs?: number; // 더블 탭 시간
    perRoute?: (name: string | null) => boolean; // true면 소비
  }
) {
  if (Capacitor.getPlatform() !== 'android') return;
  const { isIdleLocked } = useIdleLock();

  let lastBack = 0; // 최종 뒤로가기 누른 시간
  const exitDelay = opts?.topLevelExitDelayMs ?? 1500; // 뒤로가기 더블 탭 시간제한

  const onButtonBackPress = (canGoBack: boolean) => {
    if (isIdleLocked.value) return;

    const name = (router.currentRoute.value.name ?? null) as string | null;
    const isTop = !!opts?.topLevelRoutes?.includes(name ?? '');

    // 1) 인터셉터 (모달/시트 우선 종료)
    if (opts?.interceptors) {
      for (const fn of opts.interceptors) if (fn()) return;
    }

    // 2) 라우트별 커스텀 처리
    if (opts?.perRoute && opts.perRoute(name)) return;
    // 3) 최상위 라우트 처리: canGoBack 무시
    if (isTop) {
      const now = Date.now();
      if (now - lastBack < exitDelay) {
        exitApp();
      } else {
        lastBack = now;
        opts?.onTopLevelBackHint?.();
      }
      return; // 🔴 반드시 반환
    }

    // 4) 일반 뒤로가기
    if (canGoBack) {
      router.back();
      return;
    }

    // 5) 진짜 최상단이면 종료
    exitApp();
  };

  onMounted(async () => {
    await addBackButtonListener(onButtonBackPress);
  });

  onUnmounted(() => {
    removeBackButtonListener();
  });
}
